import { Chip, Grid, makeStyles, Tooltip } from '@material-ui/core';
import NotificationsActiveActive from '@material-ui/icons/NotificationsActiveOutlined';
import NotificationsOffIcon from '@material-ui/icons/NotificationsOffOutlined';
import { useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import TagManager from 'react-gtm-module';
import { useTranslation } from 'react-i18next';
import { QueryKeys } from 'src/constants';
import useApi from 'src/hooks/useApi';
import { SearchAgentDTOStrict } from 'src/hooks/useSearchAgents';
import { useSearchAgentUpdate } from 'src/hooks/useSearchAgentUpdate';
import useCustomSnackbarGlobal from 'src/hooks/useSnackbarGlobal';
import { useUser } from 'src/hooks/useUser';
import { useValuationSettings } from 'src/hooks/useValuationSettings';
import ApiService from 'src/modules/api-service';
import { getSystemAgentName } from 'src/modules/system-agents';

import { transformForSubmit } from './filter/lib';
import { useFilter } from './filter/useFilter';

type RecommendationFilterchipProps = {
  filterStateId: string | undefined;
  searchAgents: SearchAgentDTOStrict[];
};

const useStyles = makeStyles((theme) => ({
  'chip-active': {
    backgroundColor: theme.palette.intelligence.light,
    color: 'white',
    '&:hover, &:focus': {
      backgroundColor: theme.palette.intelligence.main,
    },
    '& svg': {
      color: 'white',
    },
  },
  chip: {
    color: theme.palette.intelligence.dark,
    borderColor: theme.palette.intelligence.dark,
    '& svg': {
      color: theme.palette.intelligence.dark,
    },
  },
}));

const RecommendationFilterchip = ({ filterStateId, searchAgents }: RecommendationFilterchipProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { defaultFilter, setFilter } = useFilter();
  const { data: user } = useUser();
  const { mutate: updateSearchAgent } = useSearchAgentUpdate();
  const { fetch: fetchCreate } = useApi();
  const queryClient = useQueryClient();
  const snackbar = useCustomSnackbarGlobal();
  const { valuationCountry: globalValuationCountry } = useValuationSettings();

  const recommendationSearchAgentName = getSystemAgentName(
    'recommendation',
    user?.user?.authenticationProvider ?? '',
    user?.user?.id ?? '',
  );
  const exists = searchAgents.find((agent) => agent.searchAgentDisplayName === recommendationSearchAgentName);

  function handleActivateRecommendationFilter(): void {
    setFilter({ ...defaultFilter, irisRecommendation: true, id: recommendationSearchAgentName });
  }

  async function handleToggleNotification() {
    if (exists) {
      updateSearchAgent({ ...exists, active: !exists.active });
    } else {
      TagManager.dataLayer({ dataLayer: { event: 'searchagent-save' } });
      const newSearchAgent = transformForSubmit({ ...defaultFilter, irisRecommendation: true });
      try {
        await fetchCreate(
          ApiService.searchAgent.searchAgentControllerCreateSearchAgent({
            ...newSearchAgent,
            version: '2',
            active: true,
            favorite: false,
            valuationCountry: globalValuationCountry ?? undefined,
            searchAgentDisplayName: recommendationSearchAgentName,
          }),
        );
        queryClient.invalidateQueries([QueryKeys.searchAgents]);
      } catch (error) {
        console.error('[RecommendationFilterchip] failed to create recommendation search agent', error);
        snackbar.showError(t('searchAgentsPage.messages.error'));
      }
    }
  }

  return (
    <Grid item>
      <Tooltip title={t('recommendationsPage.showRecommendations')} placement="top">
        <Chip
          size="small"
          variant={filterStateId === recommendationSearchAgentName ? 'default' : 'outlined'}
          label={t('recommendationsPage.title')}
          className={clsx(filterStateId === recommendationSearchAgentName ? classes['chip-active'] : classes.chip)}
          onClick={() => handleActivateRecommendationFilter()}
          onDelete={() => handleToggleNotification()}
          deleteIcon={exists && exists.active ? <NotificationsActiveActive /> : <NotificationsOffIcon />}
        />
      </Tooltip>
    </Grid>
  );
};

export default RecommendationFilterchip;
